import { useState, useRef, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import { es } from 'date-fns/locale';
import { parseISO } from 'date-fns';

function formatDate(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('es-ES');
}

const isMobile = () => {
  return (
    window.innerWidth <= 768 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};

const DateRangeSelector = ({
  startDate,
  endDate,
  onChange,
  label = '',
  error = false,
  helperText = '',
  minDate,
  maxDate,
  disabled = false,
  isOneWay = false,
  months = 2,
  departureDate,
  disabledDates = [],
}) => {
  const [open, setOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState('left');
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileDevice(isMobile());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (open && ref.current && !isMobileDevice) {
      const rect = ref.current.getBoundingClientRect();
      const calendarWidth = months === 2 ? 640 : 320;
      const spaceOnRight = window.innerWidth - rect.right;
      const spaceOnLeft = rect.left;

      if (spaceOnRight < calendarWidth && spaceOnLeft > calendarWidth) {
        setCalendarPosition('right');
      } else {
        setCalendarPosition('left');
      }
    }
  }, [open, months, isMobileDevice]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (isMobileDevice && open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isMobileDevice, open]);

  const handleDateChange = (ranges) => {
    const { startDate: newStart, endDate: newEnd } = ranges.selection;
    if (isOneWay) {
      onChange({ startDate: newStart, endDate: null });
      setOpen(false);
    } else {
      if (newStart && !newEnd) {
        onChange({ startDate: newStart, endDate: newStart });
      } else {
        onChange({ startDate: newStart, endDate: newEnd });
        if (newEnd && newEnd !== newStart) {
          setOpen(false);
        }
      }
    }
  };

  const minDateFromDeparture = departureDate ? parseISO(departureDate) : minDate;

  useEffect(() => {
    if (departureDate && !startDate && !isOneWay) {
      const departureDateObj = parseISO(departureDate);
      onChange({ startDate: departureDateObj, endDate: departureDateObj });
    }
  }, [departureDate, startDate, isOneWay, onChange]);

  const getCalendarStyle = () => {
    if (isMobileDevice) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        width: '98vw',
        maxWidth: '97vw',
        maxHeight: '85vh',
        overflow: 'auto',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      };
    } else {
      return {
        position: 'absolute',
        zIndex: 10,
        top: 48,
        left: '50%',
        transform: 'translateX(-50%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        backgroundColor: '#fff',
      };
    }
  };

  const renderMobileOverlay = () => {
    if (!isMobileDevice || !open) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 999,
        }}
        onClick={() => setOpen(false)}
      />
    );
  };

  return (
    <>
      {renderMobileOverlay()}
      <div ref={ref} style={{ position: 'relative', width: '100%' }}>
        <label
          style={{
            fontSize: 12,
            color: '#4f4f4f',
            display: 'block',
            marginBottom: 4,
          }}
        >
          {label}
        </label>
        <div
          className={`form__field date-picker ${error ? 'dates-error' : ''}`}
          onClick={() => !disabled && setOpen(!open)}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            height: 40,
            lineHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: error ? '1px solid rgb(207, 24, 0)' : '1px solid #ccc',
            borderRadius: 4,
            padding: '0 12px',
            background: disabled ? '#f5f5f5' : '#fff',
            gap: 8,
            flexDirection: 'row',
          }}
        >
          {isOneWay ? (
            <span
              style={{
                color: !startDate ? '#aaa' : '#222',
                whiteSpace: 'nowrap',
                fontSize: 16,
                display: 'inline-block',
              }}
            >
              {startDate ? formatDate(startDate) : 'Ida'}
            </span>
          ) : (
            <>
              <span
                style={{
                  color: !startDate ? '#aaa' : '#222',
                  whiteSpace: 'nowrap',
                  fontSize: 16,
                  display: 'inline-block',
                }}
              >
                {startDate ? formatDate(startDate) : 'Ida'}
              </span>
              <span style={{ color: '#aaa', fontSize: 18, display: 'inline-block' }}>→</span>
              <span
                style={{
                  color: !endDate ? '#aaa' : '#222',
                  whiteSpace: 'nowrap',
                  fontSize: 16,
                  display: 'inline-block',
                }}
              >
                {endDate ? formatDate(endDate) : 'Vuelta'}
              </span>
            </>
          )}
        </div>
        {helperText && error && (
          <div style={{ color: 'rgb(207, 24, 0)', fontSize: 11, marginTop: 2 }}>{helperText}</div>
        )}
        {open && (
          <div style={getCalendarStyle()}>
            {isMobileDevice && (
              <div
                style={{
                  padding: '16px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '500' }}>
                  Seleccionar fechas
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <div
              style={{
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <DateRange
                direction='horizontal'
                disabledDates={disabledDates}
                locale={es}
                maxDate={maxDate}
                minDate={minDateFromDeparture}
                months={isMobileDevice ? 1 : months || 2}
                onChange={handleDateChange}
                rangeColors={['#1976d2']}
                ranges={[
                  {
                    startDate: startDate || new Date(),
                    endDate: isOneWay
                      ? startDate || new Date()
                      : endDate || startDate || new Date(),
                    key: 'selection',
                  },
                ]}
                retainEndDateOnFirstSelection={false}
                showMonthAndYearPickers={false}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DateRangeSelector;
